import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Hook: returns true only after `delayMs`. Lets us avoid the dreaded
 * "skeleton flash" for fast loads where data resolves in <200ms.
 */
function useDelayedShow(delayMs = 200) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setShow(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);
  return show;
}

/** Single skeleton row sized to match the search result vet cards. */
export const VetCardSkeleton = () => (
  <Card className="border border-border">
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-1/2" />
        </div>
        <div className="flex flex-col items-end gap-2">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-16" />
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mt-3">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-20" />
      </div>
      <div className="flex gap-2 mt-4">
        <Skeleton className="h-9 w-28" />
        <Skeleton className="h-9 w-24" />
      </div>
    </CardContent>
  </Card>
);

interface VetCardSkeletonListProps {
  /** How many skeleton cards to render. Defaults to 5. */
  count?: number;
  /** Delay before the skeletons appear, to avoid a flash on fast loads. */
  delayMs?: number;
  /** Accessible label announced to screen readers while loading. */
  label?: string;
}

/**
 * Renders a list of skeleton vet cards. Hidden for the first `delayMs`
 * (default 200ms) so quick loads never flash a loading state.
 */
const VetCardSkeletonList = ({
  count = 5,
  delayMs = 200,
  label = "Loading vets",
}: VetCardSkeletonListProps) => {
  const show = useDelayedShow(delayMs);
  if (!show) return null;
  return (
    <div
      className="space-y-4"
      role="status"
      aria-live="polite"
      aria-label={label}
    >
      {Array.from({ length: count }).map((_, i) => (
        <VetCardSkeleton key={i} />
      ))}
      <span className="sr-only">{label}…</span>
    </div>
  );
};

export default VetCardSkeletonList;
